// ── Чистая игровая логика: XP, уровни, достижения, квесты ──
// Всё вычисляется как функция от weekData (никаких скрытых флагов на сервере).

export const LEVELS = [
  { xp: 0, level: 1, title: 'Юный талант' },
  { xp: 100, level: 2, title: 'Перспективный' },
  { xp: 250, level: 3, title: 'Игрок старта' },
  { xp: 500, level: 4, title: 'Основной состав' },
  { xp: 1000, level: 5, title: 'Капитан' },
  { xp: 2000, level: 6, title: 'Легенда академии' },
];

const START = { sprint: 6.43, slalomNo: 6.28, slalomBall: 9.6, juggle: 0, weak: 0 };

function weeksArr(weekData) {
  return Object.values(weekData || {}).filter(Boolean);
}

function bestSprint(weekData) {
  const vals = weeksArr(weekData).map((d) => d.sprint).filter((v) => v != null);
  return vals.length ? Math.min(...vals) : null;
}

function bestBall(weekData) {
  const vals = weeksArr(weekData).map((d) => d.slalomBall).filter((v) => v != null);
  return vals.length ? Math.min(...vals) : null;
}

function maxJuggle(weekData) {
  return Math.max(0, ...weeksArr(weekData).map((d) => d.juggle || 0));
}

function maxWeak(weekData) {
  return Math.max(0, ...weeksArr(weekData).map((d) => d.weak || 0));
}

function bestBallDiffPct(weekData) {
  let best = 53;
  weeksArr(weekData).forEach((d) => {
    if (d.slalomNo && d.slalomBall) {
      const diff = ((d.slalomBall - d.slalomNo) / d.slalomNo) * 100;
      if (diff < best) best = diff;
    }
  });
  return best;
}

function longestStreak(weekData) {
  const keys = Object.keys(weekData || {})
    .filter((k) => weekData[k])
    .map(Number)
    .sort((a, b) => a - b);
  let best = keys.length ? 1 : 0;
  let cur = 1;
  for (let i = 1; i < keys.length; i++) {
    cur = keys[i] === keys[i - 1] + 1 ? cur + 1 : 1;
    best = Math.max(best, cur);
  }
  return best;
}

// ── FIFA-характеристики ──

export function computeStats(weekData) {
  const sprint = bestSprint(weekData);
  const ball = bestBall(weekData);
  const juggle = maxJuggle(weekData);
  const weak = maxWeak(weekData);
  const clamp = (v) => Math.round(Math.min(85, Math.max(30, v)));

  if (sprint == null) {
    return { pac: 35, dri: 35, pas: 35, sho: 35, def: 35, phy: 20, overall: 33 };
  }

  const pac = clamp(35 + (START.sprint - sprint) * 18);
  const dri = clamp(35 + (START.slalomBall - (ball ?? START.slalomBall)) * 5);
  const pas = clamp(35 + Math.min(juggle, 50) * 0.35);
  const sho = clamp(35 + Math.min(weak, 30) * 0.5);
  const def = 35;
  const phy = 20;
  const overall = Math.round((pac + dri + pas + sho + def + phy) / 6);
  return { pac, dri, pas, sho, def, phy, overall };
}

export function tierForOverall(overall) {
  if (overall >= 67) return 'gold';
  if (overall >= 40) return 'silver';
  return 'bronze';
}

// ── Жонглёж: промежуточные рубежи (5/10/15/20 раз) ──
// Используются и для квестов, и для достижений — отдельные записи на каждый рубеж.

const JUGGLE_MILESTONES = [
  { n: 5, icon: '🥉', questXp: 10, achXp: 5 },
  { n: 10, icon: '🥈', questXp: 15, achXp: 10 },
  { n: 15, icon: '🥇', questXp: 20, achXp: 15 },
  { n: 20, icon: '🏆', questXp: 25, achXp: 20 },
];

// ── Достижения ──

export const ACHIEVEMENT_DEFS = [
  { id: 'first', emoji: '⚽', name: 'Первый шаг', desc: 'Сохранена хотя бы одна неделя тренером', xp: 10 },
  { id: 'rocket', emoji: '🚀', name: 'Ракета', desc: 'Спринт 30м < 6.0 сек в любую неделю', xp: 20 },
  { id: 'juggler', emoji: '🎪', name: 'Жонглёр', desc: 'Жонглёж обеими ногами ≥ 30 раз', xp: 20 },
  { id: 'weakfoot', emoji: '🦶', name: 'Слабая нога', desc: 'Жонглёж слабой ногой ≥ 20 раз', xp: 20 },
  { id: 'control', emoji: '🎯', name: 'Контроль мяча', desc: 'Разница слалома с/без ≤ 30%', xp: 30 },
  { id: 'streak', emoji: '🔥', name: 'Огонь', desc: 'Данные заполнены 3 недели подряд', xp: 20 },
  { id: 'speedup', emoji: '💨', name: 'Реактивный', desc: 'Спринт улучшен на ≥ 0.5 сек vs старт (6.43)', xp: 30 },
  ...JUGGLE_MILESTONES.map((m) => ({
    id: `juggle${m.n}`,
    emoji: m.icon,
    name: `Жонглёж: ${m.n} раз`,
    desc: `Жонглёж обеими ногами ≥ ${m.n} раз`,
    xp: m.achXp,
  })),
  { id: 'captain', emoji: '👑', name: 'Капитан лета', desc: 'Выполнены все квесты лета', xp: 60 },
];

export function computeAchievements(weekData, questsDoneAll) {
  const sprint = bestSprint(weekData);
  const juggle = maxJuggle(weekData);
  const weak = maxWeak(weekData);
  const diffPct = bestBallDiffPct(weekData);
  const hasAnyWeek = weeksArr(weekData).length > 0;
  const streak = longestStreak(weekData);

  const unlockedMap = {
    first: hasAnyWeek,
    rocket: sprint != null && sprint < 6.0,
    juggler: juggle >= 30,
    weakfoot: weak >= 20,
    control: hasAnyWeek && diffPct <= 30,
    streak: streak >= 3,
    speedup: sprint != null && sprint <= START.sprint - 0.5,
    captain: !!questsDoneAll,
  };
  JUGGLE_MILESTONES.forEach((m) => { unlockedMap[`juggle${m.n}`] = juggle >= m.n; });

  return ACHIEVEMENT_DEFS.map((a) => ({ ...a, unlocked: !!unlockedMap[a.id] }));
}

// ── Квесты ──

export const QUEST_DEFS = [
  { id: 'q1', icon: '🎯', name: 'Старт сезона', desc: 'Заполни данные минимум за 3 недели подряд', xp: 20 },
  { id: 'q2', icon: '🎪', name: 'Плеймейкер', desc: 'Жонглёж обеими ногами 30+ раз', xp: 30 },
  { id: 'q3', icon: '⚽', name: 'Контроль мяча', desc: 'Разница слалома с/без мяча ≤ 30%', xp: 30 },
  { id: 'q4', icon: '🚀', name: 'Ракета', desc: 'Спринт 30м быстрее 6.0 сек', xp: 40 },
  ...JUGGLE_MILESTONES.map((m) => ({
    id: `q_j${m.n}`,
    icon: m.icon,
    name: `Жонглёж: ${m.n} раз`,
    desc: `Жонглёж обеими ногами — ${m.n} раз`,
    xp: m.questXp,
  })),
  { id: 'q5', icon: '👑', name: 'Капитан лета', desc: 'Выполни все квесты выше', xp: 60 },
];

export function computeQuests(weekData) {
  const streak = longestStreak(weekData);
  const juggle = maxJuggle(weekData);
  const diffPct = bestBallDiffPct(weekData);
  const sprint = bestSprint(weekData);

  const q1Val = Math.min(streak, 3);
  const q1Pct = Math.round((q1Val / 3) * 100);

  const q2Val = Math.min(juggle, 30);
  const q2Pct = Math.round((q2Val / 30) * 100);

  const q3Pct = Math.round(Math.min(100, Math.max(0, ((53 - diffPct) / (53 - 30)) * 100)));

  const bestSprintVal = sprint ?? START.sprint;
  const q4Pct = Math.round(Math.min(100, Math.max(0, ((START.sprint - bestSprintVal) / (START.sprint - 6.0)) * 100)));

  const quests = [
    { ...QUEST_DEFS[0], locked: false, done: q1Pct >= 100, progress: { val: q1Val, total: 3, pct: q1Pct, label: 'недель подряд', isPct: false } },
    { ...QUEST_DEFS[1], locked: false, done: q2Pct >= 100, progress: { val: q2Val, total: 30, pct: q2Pct, label: 'из 30 раз', isPct: false } },
    { ...QUEST_DEFS[2], locked: false, done: q3Pct >= 100, progress: { pct: q3Pct, label: `Разница сейчас: ${Math.round(diffPct)}% → цель 30%`, isPct: true } },
    { ...QUEST_DEFS[3], locked: false, done: q4Pct >= 100, progress: { pct: q4Pct, label: `Лучший: ${bestSprintVal.toFixed(2)} сек → цель < 6.0`, isPct: true } },
  ];

  JUGGLE_MILESTONES.forEach((m, i) => {
    const val = Math.min(juggle, m.n);
    const pct = Math.round((val / m.n) * 100);
    quests.push({
      ...QUEST_DEFS[4 + i],
      locked: false,
      done: pct >= 100,
      progress: { val, total: m.n, pct, label: 'раз', isPct: false },
    });
  });

  const doneCount = quests.filter((q) => q.done).length;
  const allDone = doneCount === quests.length;
  quests.push({
    ...QUEST_DEFS[QUEST_DEFS.length - 1],
    locked: !allDone,
    done: allDone,
    progress: { val: doneCount, total: quests.length, pct: Math.round((doneCount / quests.length) * 100), label: 'квестов выполнено', isPct: false },
  });

  return quests;
}

export function totalXp(weekData) {
  const quests = computeQuests(weekData);
  return quests.filter((q) => q.done).reduce((s, q) => s + q.xp, 0);
}

export function levelInfo(xp) {
  let lvl = LEVELS[0];
  for (let i = LEVELS.length - 1; i >= 0; i--) {
    if (xp >= LEVELS[i].xp) { lvl = LEVELS[i]; break; }
  }
  const next = LEVELS[lvl.level] || { xp: lvl.xp + 1000 };
  const pct = Math.min(100, ((xp - lvl.xp) / (next.xp - lvl.xp)) * 100);
  return { ...lvl, nextXp: next.xp, pct };
}

const DAYS = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];
export function formatDate(d) {
  if (!d) return '—';
  const dt = new Date(d + 'T00:00:00');
  if (Number.isNaN(dt.getTime())) return '—';
  return `${DAYS[dt.getDay()]}, ${dt.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })}`;
}
